<template>
  <div class="product-card-wrapper pt-0 px-0"> <!-- Elemento raíz único -->
    <!-- Product Card -->
    <div class="product-card">      <div class="card text-white">     

        <!-- Product image -->
        <a :href="`/producto/${product.id}`" class="card-img-wrapper">
          <div class="image-container">
            <!-- Badge de descuento porcentual -->
            <div v-if="productDiscount > 0" class="promotion-badge discount-badge">
              <span class="badge bg-success text-dark">
                {{ Math.round(productDiscount) }}% OFF
              </span>
            </div>
            
            <!-- Badge de promoción especial personalizada -->
            <div v-else-if="product.has_special_promotion && product.special_promotion_name" class="promotion-badge">
              <span class="badge bg-warning text-dark">
                <FaIcon name="fire" class="me-1" />{{ product.special_promotion_name.toUpperCase() }}
              </span>
            </div>
            
            <!-- Badge de promoción legacy (por compatibilidad) -->
            <div v-else-if="product.promotion_type" class="promotion-badge">
              <span class="badge bg-danger">
                <FaIcon name="tags" class="me-1" />{{ product.promotion_type.toUpperCase() }}
              </span>
            </div>
            
            <img 
              :src="mainImageSrc" 
              :alt="product.name"
              class="card-img-top product-image"
              :class="product.image ? 'p-0' : 'p-4'"
              loading="lazy"
              decoding="async"
            >
          </div>
        </a>

        <!-- Product info -->
        <div class="card-body d-flex flex-column justify-content-between">
          <div class="product-info">
            <div>
              <span class="brand-tag" style="border: 1px solid var(--primary-color)">
                {{ product.category_name }}
              </span>
            </div>
            <h3 class="product-name text-white fw-semibold">{{ product.name }}</h3>
              <div class="price-section">
              <div class="price-container">
                <!-- Current price (final price with discount applied) -->
                <div class="d-flex align-items-center flex-wrap">
                  <div class="current-price me-2">
                    <template v-if="priceRange.hasRange && productDiscount > 0">
                      {{ product.currency === 'USD' ? 'USD ' : '$ ' }}{{ formatPrice(getDiscountedPrice(priceRange.min)) }} - 
                      {{ product.currency === 'USD' ? 'USD ' : '$ ' }}{{ formatPrice(getDiscountedPrice(priceRange.max)) }}
                    </template>
                    <template v-else-if="priceRange.hasRange">
                      {{ product.currency === 'USD' ? 'USD ' : '$ ' }}{{ formatPrice(priceRange.min) }} - {{ product.currency === 'USD' ? 'USD ' : '$ ' }}{{ formatPrice(priceRange.max) }}
                    </template>
                    <template v-else>
                      {{ product.currency === 'USD' ? 'USD ' : '$ ' }}{{ formatPrice(finalPrice) }}
                    </template>
                  </div>
                  
                  <span v-if="product.has_variants" 
                        class="variant-badge ms-1" 
                        title="Producto con variantes">
                    <FaIcon name="layer-group" />
                  </span>
                </div>
                <!-- Original price (if discounted) -->
                <div class="original-price-container d-flex align-items-center mb-0" v-if="productDiscount > 0">
                  <div class="original-price text-white-50 fw-semibold">
                    <template v-if="priceRange.hasRange">
                      {{ product.currency === 'USD' ? 'USD ' : '$ ' }}{{ formatPrice(priceRange.min) }} - {{ product.currency === 'USD' ? 'USD ' : '$ ' }}{{ formatPrice(priceRange.max) }}
                    </template>
                    <template v-else>
                      {{ product.currency === 'USD' ? 'USD ' : '$ ' }}{{ formatPrice(productPrice) }}
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Single button: add to cart (no variants) or see variants -->
          <div class="card-actions d-flex">
            <button
              v-if="!product.has_variants"
              class="btn btn-primary w-100 add-cart-btn"
              :class="`cart-state-${cartState}`"
              @click="quickAddToCart"
              :disabled="!canQuickAdd"
            >
              <span class="btn-content">
                <template v-if="cartState === 'success'">
                  <FaIcon name="check-circle" class="me-2" />
                  Agregado
                </template>
                <template v-else>
                  <FaIcon name="shopping-cart" class="me-2" />
                  {{ canQuickAdd ? 'Agregar al carrito' : 'Sin stock' }}
                </template>
              </span>
            </button>
            <a 
              v-else
              :href="`/producto/${product.id}`"
              class="btn btn-primary w-100"
              title="Ver variantes disponibles"
            >
              <FaIcon name="layer-group" class="me-2" />
              Ver variantes
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, computed, ref, onBeforeUnmount } from 'vue'
import { formatPrice } from "@/helpers/product"
import { cleanMediaUrl } from '@/utils/text'
import { useStore } from 'vuex'

const store = useStore()

const fallbackImg = require('@/assets/images/jamrock-logo.webp')

const props = defineProps({
  product: {
    type: Object,
    required: true,
    default: () => ({
      id: null,
      name: '',
      category: '',
      subcategory: '',
      price: 0,
      price_sparta: 0,
      stock: 0,
      description: '',
      description_sparta: '',
      features: [],
      brand: '',
      image: null,
      discount: 0,
      discount_sparta: 0,
      is_available: true,
      is_available_sparta: true
    })
  }
})

const mainImageSrc = computed(() => {
  const u = cleanMediaUrl(props.product.image)
  return u || fallbackImg
})

// Computed properties for product data - Simplificados para usar directamente los valores de la API
// Obtener precios directamente de la API, evitando dependencias circulares
const productPrice = computed(() => {
  // Usar directamente price.parsedValue de la API
  if (props.product.price && typeof props.product.price === 'object' && props.product.price.parsedValue !== undefined) {
    return Number(props.product.price.parsedValue);
  }
  const basePrice = props.product.price || 0;
  return Number(basePrice);
});

// Obtener el descuento directamente, sin depender de finalPrice
const productDiscount = computed(() => {
  // Usar directamente total_discount si existe
  if (props.product.total_discount !== undefined && props.product.total_discount !== null) {
    // Si es un objeto con parsedValue (API nueva)
    if (typeof props.product.total_discount === 'object' && props.product.total_discount.parsedValue !== undefined) {
      return Number(props.product.total_discount.parsedValue);
    }
    // Si es un valor directo (API antigua)
    if (typeof props.product.total_discount === 'number' || typeof props.product.total_discount === 'string') {
      return Number(props.product.total_discount);
    }
  }
  
  // Usar el campo discount si existe
  if (props.product.discount !== undefined && props.product.discount !== null) {
    if (typeof props.product.discount === 'object' && props.product.discount.parsedValue !== undefined) {
      return Number(props.product.discount.parsedValue);
    }
    if (typeof props.product.discount === 'number' || typeof props.product.discount === 'string') {
      return Number(props.product.discount);
    }
  }
  
  // Si nada más, usar 0
  return 0;
});

// Obtener el precio final, usando el precio original y el descuento
const finalPrice = computed(() => {
  // Usar directamente final_price de la API si existe como número
  if (props.product.final_price !== undefined && props.product.final_price !== null) {
    // Si es un objeto con parsedValue (formato legacy)
    if (typeof props.product.final_price === 'object' && props.product.final_price.parsedValue !== undefined) {
      return Number(props.product.final_price.parsedValue);
    }
    // Si es un número directo (formato actual de la API)
    if (typeof props.product.final_price === 'number' || typeof props.product.final_price === 'string') {
      return Number(props.product.final_price);
    }
  }
  
  // Si no hay final_price, calcularlo manualmente
  const price = productPrice.value;
  const discount = productDiscount.value;
  
  if (!discount || discount <= 0) return price;
  
  const discountAmount = (price * discount) / 100;
  return price - discountAmount;
});

// Add new computed property for price range
const priceRange = computed(() => {
  if (!props.product.has_variants || !props.product.variants || !props.product.variants.length) {
    return {
      hasRange: false,
      min: productPrice.value,
      max: productPrice.value
    };
  }

  // Obtener el precio base considerando la estructura de datos de la API
  const basePrice = productPrice.value;
  let minPrice = basePrice;
  let maxPrice = basePrice;
  let hasAdjustments = false;

  // Calculate min and max prices considering all variants
  props.product.variants.forEach(variant => {
    if (variant.options && Array.isArray(variant.options)) {
      variant.options.forEach(option => {
        if (option.price_adjustment !== undefined) {
          const adjustedPrice = basePrice + Number(option.price_adjustment);
          minPrice = Math.min(minPrice, adjustedPrice);
          maxPrice = Math.max(maxPrice, adjustedPrice);
          if (option.price_adjustment !== 0) {
            hasAdjustments = true;
          }
        }
      });
    }
  });

  return {
    hasRange: hasAdjustments,
    min: minPrice,
    max: maxPrice
  };
});

// Helper function to get discounted price
function getDiscountedPrice(price) {
  const discount = productDiscount.value;
  if (!discount || discount <= 0) return price;
  const discountAmount = (price * discount) / 100;
  return price - discountAmount;
}

// Products without variants can be added to cart directly from the card
const canQuickAdd = computed(() => {
  if (props.product.is_available === false) return false;
  const stock = props.product.stock;
  if (stock === undefined || stock === null) return true;
  return Number(stock) > 0;
});

// --- Feedback del botón add-to-cart (card) ---
// Sin animación de carga: click -> success ("Agregado" + check) -> vuelve a idle.
// La validación real de stock ocurre en el carrito; acá es solo feedback visual.
const SUCCESS_DURATION = 1600;   // ms que muestra el estado success
const cartState = ref('idle');
let stateTimer = null;

const resetCartState = () => {
  cartState.value = 'idle';
  stateTimer = null;
};

const quickAddToCart = () => {
  if (cartState.value !== 'idle' || !canQuickAdd.value) return;

  store.dispatch('addToCart', {
    id: props.product.id,
    name: props.product.name,
    price: finalPrice.value,
    original_price: productPrice.value,
    brand: props.product.brand,
    image: props.product.image,
    category_id: props.product.category_id,
    quantity: 1,
    currency: props.product.currency || 'ARS',
    discount: productDiscount.value,
    total_discount: productDiscount.value,
    has_special_promotion: props.product.has_special_promotion || false,
    special_promotion_name: props.product.special_promotion_name || null
  });

  cartState.value = 'success';
  stateTimer = setTimeout(resetCartState, SUCCESS_DURATION);
};

onBeforeUnmount(() => {
  if (stateTimer !== null) clearTimeout(stateTimer);
});
</script>

<style scoped>
.product-card-wrapper {
  width: 100%;
  max-width: 300px;
  margin: 0 auto;
}

.product-card {
  background-color: var(--dark-color);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease-in-out;
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Bootstrap .card pinta fondo blanco (--bs-card-bg); hay que anularlo en la card de producto */
.product-card > .card {
  --bs-card-bg: var(--black-color-2);
  --bs-card-border-color: rgba(255, 255, 255, 0.1);
  background-color: var(--black-color-2);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.product-card:hover {
  transform: translateY(-5px);
}

.card-img-wrapper {
  display: block;
  overflow: hidden;
  border-bottom: 1px solid var(--primary-color);
}

.image-container {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  min-height: 180px;
  overflow: hidden;
}

.promotion-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
}

.promotion-badge .badge {
  font-weight: 800;
  text-shadow: none;
  padding: 6px 12px;
  font-size: 13px;
  animation: pulse 2s ease-in-out infinite;
}

.promotion-badge.discount-badge .badge {
  background-color: var(--primary-color) !important;
  color: var(--black-color) !important;
}

.promotion-badge .badge.bg-warning {
  background-color: #ffc107 !important;
  color: #000 !important;
}

.promotion-badge .badge.bg-danger {
  background-color: #dc3545 !important;
  color: white !important;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.product-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.card-body {
  padding: 16px;
  height: 260px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.product-info {
  margin-bottom: 16px;
  flex: 1;
}

.brand-tag {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 10px !important;
  color: var(--primary-color);
  background-color: var(--dark-color);
}

.product-name {
  font-size: 16px;
  margin: 8px 0;
  font-weight: 500 !important;
}

.price-section {
  margin-bottom: 16px;
}

.price-container {
  display: flex;
  flex-direction: row;
  gap: 5px;
}

.original-price-container {
  margin-bottom: 4px;
}

.original-price {
  text-decoration: line-through;
  color: var(--light-color);
  font-size: 14px;
  font-weight: 500;
}

.current-price {
  font-size: 16px;  
  font-weight: bold;
  color: #dfdfdf;
}

.save-badge {
  background-color: var(--success-color);
  color: var(--dark-color);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  margin-left: 8px;
}

.variant-badge {
  background-color: var(--info-color);
  color: var(--dark-color);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
}

.card-actions {
  margin-top: auto;
}

.btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  color: var(--dark-color);
  background-color: var(--primary-color);
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
}

.btn:hover {
  background-color: var(--primary-color-hover);
}

.btn i {
  margin-right: 4px;
}

@media (max-width: 767.98px) {
  .product-card-wrapper {
    max-width: 100%;
  }
  
  .card-body {
    min-height: 250px;
    height: auto;
    max-height: 320px;
  }
  
  .image-container {
    height: 250px;
  }
}

@media (max-width: 480px) {
  .product-card-wrapper {
    max-width: 100%;
  }
  
  .image-container {
    height: 220px;
  }
  
  .card-body {
    padding: 12px;
    min-height: 220px;
  }
  
  .product-name {
    font-size: 16px;
  }
  
  .current-price {
    font-size: 18px;
  }
  
  .brand-tag {
    font-size: 8px;
    padding: 3px 6px;
  }
}

/* Botón add-to-cart con feedback de éxito (estilo MercadoPago) */
.add-cart-btn {
  position: relative;
  overflow: hidden;
  isolation: isolate;
}

.btn-content {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.cart-state-success {
  background-color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
  animation: cart-success-pop 0.35s ease;
}

@keyframes cart-success-pop {
  0% { transform: scale(1); }
  45% { transform: scale(1.04); }
  100% { transform: scale(1); }
}
</style>